// Icons of the applications of the centre (Apps.jsx, sidebar, phone menu): one family, drawn for Jamora. Same grid (24 px), same line
// (1.75, round), and the same two-tone rule — the outline takes the application's colour (currentColor) and ONE shape per icon carries a
// soft tint of it (`tint`). The pharmacy cross of the Jamora mark comes back as a motif where the domain is the pharmacy itself
// (customers, support) so the family reads as ours, not as a generic set. Props mirror lucide's (`size`, `className`), so an icon drops in
// wherever a lucide one was used.
const TINT = { fill: "currentColor", fillOpacity: 0.16, stroke: "none" };

function make(name, draw) {
  const Icon = ({ size = 24, className, ...rest }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"
      strokeLinejoin="round" className={className} aria-hidden="true" focusable="false" {...rest}>
      {draw}
    </svg>
  );
  Icon.displayName = name;
  return Icon;
}

export const CommercialIcon = make("CommercialIcon", (
  <>
    <path d="M3.5 5h17l-6.3 7.4V19l-4.4 2v-8.6z" {...TINT} />
    <path d="M3.5 5h17l-6.3 7.4V19l-4.4 2v-8.6z" />
    <path d="M7.5 8.2h9" />
  </>
));

export const CustomersIcon = make("CustomersIcon", (
  <>
    <path d="M5 10h14v10H5z" {...TINT} />
    <path d="M3.5 10 5 4.5h14L20.5 10" />
    <path d="M3.5 10a2.75 2.75 0 0 0 5.5 0 2.75 2.75 0 0 0 6 0 2.75 2.75 0 0 0 5.5 0" />
    <path d="M5 11.2V20h14v-8.8" />
    <path d="M12 13v4.4M9.8 15.2h4.4" />
  </>
));

export const RevenueIcon = make("RevenueIcon", (
  <>
    <path d="M3.5 4.5a1 1 0 0 1 1-1h7.6a1 1 0 0 1 .7.3l7.4 7.4a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-7.4-7.4a1 1 0 0 1-.3-.7z" {...TINT} />
    <path d="M3.5 4.5a1 1 0 0 1 1-1h7.6a1 1 0 0 1 .7.3l7.4 7.4a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-7.4-7.4a1 1 0 0 1-.3-.7z" />
    <circle cx="8" cy="8" r="1.1" />
    <path d="m10.8 15.2 2.4-2.4 1.6 1.6 2.6-2.6" />
  </>
));

export const SupportIcon = make("SupportIcon", (
  <>
    <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H11l-4.5 4v-4h-1A1.5 1.5 0 0 1 4 14.5z" {...TINT} />
    <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H11l-4.5 4v-4h-1A1.5 1.5 0 0 1 4 14.5z" />
    <path d="M12 7.2v5.6M9.2 10h5.6" />
  </>
));

export const ProductIcon = make("ProductIcon", (
  <>
    <path d="M12 3 20 7.2 12 11.5 4 7.2z" {...TINT} />
    <path d="M12 3l8 4.2v9.6L12 21l-8-4.2V7.2z" />
    <path d="m4 7.2 8 4.3 8-4.3M12 11.5V21" />
  </>
));

export const DocumentsIcon = make("DocumentsIcon", (
  <>
    <circle cx="16.5" cy="17.2" r="3.7" {...TINT} />
    <path d="M13.5 3H6v18h5.5M13.5 3 18 7.5v4M13.5 3v4.5H18" />
    <path d="M9.2 11.5h5M9.2 14.5h2.6" />
    <circle cx="16.5" cy="17.2" r="3.7" />
    <path d="m14.9 17.3 1.2 1.2 2.1-2.3" />
  </>
));

export const FieldIcon = make("FieldIcon", (
  <>
    <path d="M12 17.8s-6-5-6-9.3a6 6 0 0 1 12 0c0 4.3-6 9.3-6 9.3z" {...TINT} />
    <path d="M12 17.8s-6-5-6-9.3a6 6 0 0 1 12 0c0 4.3-6 9.3-6 9.3z" />
    <circle cx="12" cy="8.5" r="2.2" />
    <path d="M8 19.2c-2.2.5-3.5 1.2-3.5 2s3.4 1.6 7.5 1.6 7.5-.8 7.5-1.6-1.3-1.5-3.5-2" />
  </>
));

export const FinanceIcon = make("FinanceIcon", (
  <>
    <path d="M4 8.5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" {...TINT} />
    <path d="M4 8.5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
    <path d="M6.5 6.5 15 3.6a1 1 0 0 1 1.3.9v2" />
    <path d="M20 11.8h-3.5a2.2 2.2 0 0 0 0 4.4H20" />
    <circle cx="16.6" cy="14" r=".5" />
  </>
));

export const AdminIcon = make("AdminIcon", (
  <>
    <path d="M12 3 19.5 6v5.5c0 4.5-3.2 8.2-7.5 9.5-4.3-1.3-7.5-5-7.5-9.5V6z" {...TINT} />
    <path d="M12 3 19.5 6v5.5c0 4.5-3.2 8.2-7.5 9.5-4.3-1.3-7.5-5-7.5-9.5V6z" />
    <circle cx="12" cy="10.2" r="1.8" />
    <path d="M12 12v3.2" />
  </>
));

export const PurchasesIcon = make("PurchasesIcon", (
  <>
    <path d="M5 8h14l-1 12.5H6z" {...TINT} />
    <path d="M5 8h14l-1 12.5H6z" />
    <path d="M9 8V6.5a3 3 0 0 1 6 0V8" />
    <path d="M9.5 12.5h5" />
  </>
));

export const MarketingIcon = make("MarketingIcon", (
  <>
    <path d="M4 10v4a1 1 0 0 0 1 1h2.5l7.5 4V5L7.5 9H5a1 1 0 0 0-1 1z" {...TINT} />
    <path d="M4 10v4a1 1 0 0 0 1 1h2.5l7.5 4V5L7.5 9H5a1 1 0 0 0-1 1z" />
    <path d="M18 9.5a3.5 3.5 0 0 1 0 5M20.5 7a7 7 0 0 1 0 10" />
  </>
));

export const GovernanceIcon = make("GovernanceIcon", (
  <>
    <path d="M3.5 9 12 4l8.5 5z" {...TINT} />
    <path d="M3.5 9 12 4l8.5 5z" />
    <path d="M6.5 11.5v6M10.2 11.5v6M13.8 11.5v6M17.5 11.5v6M4 20.5h16" />
  </>
));

export const CopilotIcon = make("CopilotIcon", (
  <>
    <path d="m10 3.5 1.8 4.7 4.7 1.8-4.7 1.8L10 16.5l-1.8-4.7L3.5 10l4.7-1.8z" {...TINT} />
    <path d="m10 3.5 1.8 4.7 4.7 1.8-4.7 1.8L10 16.5l-1.8-4.7L3.5 10l4.7-1.8z" />
    <path d="m18 14.8.8 2.1 2.1.8-2.1.8-.8 2.1-.8-2.1-2.1-.8 2.1-.8z" />
  </>
));

export const CockpitIcon = make("CockpitIcon", (
  <>
    <path d="M4 17a8 8 0 0 1 16 0z" {...TINT} />
    <path d="M4 17a8 8 0 1 1 16 0" />
    <path d="m12 17 3.8-4.6" />
    <path d="M3 20.5h18" />
  </>
));

export const TeamIcon = make("TeamIcon", (
  <>
    <path d="M3.5 19.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5z" {...TINT} />
    <circle cx="9" cy="8" r="3" />
    <path d="M3.5 19.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
    <circle cx="16.8" cy="9" r="2.4" />
    <path d="M16.8 14.2c2.6 0 4.2 1.6 4.2 4.3" />
  </>
));


// The applications launcher: nine dots. Distinct from the dashboard's four squares, so « Applications » is never read as a page.
export const LauncherIcon = make("LauncherIcon", (
  <g fill="currentColor" stroke="none">
    {[5, 12, 19].flatMap((y) => [5, 12, 19].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.7" />))}
  </g>
));

// Jamora App: the REAL Jamora mark in its own colours (navy and teal), not a tinted line icon — it is the product, not a section of the CRM.
// The navy part of the mark follows `--jamora-icon-navy`: white on the dark navy sidebar and bottom bar (set there), brand navy everywhere else.
export function JamoraAppIcon({ size = 24, className, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false" {...rest}>
      <path style={{ fill: "var(--jamora-icon-navy, #083346)" }} d="M9.2 2H14.5C15.55 2 16.4 2.85 16.4 3.9V7.2C13.25 8.15 11.55 10.2 10 13.25C8.45 16.3 6.65 17.1 3.9 17.1H3C1.9 17.1 1 16.2 1 15.1V10.5C1 9.4 1.9 8.5 3 8.5H7.2V4C7.2 2.9 8.1 2 9.2 2Z" />
      <path fill="#06bcb0" d="M18.1 8.5H21C22.1 8.5 23 9.4 23 10.5V15.1C23 16.2 22.1 17.1 21 17.1H16.4V21.4C16.4 22.5 15.5 23.4 14.4 23.4H9.2C8.1 23.4 7.2 22.5 7.2 21.4V18.3C10.25 17.05 11.75 15.5 13.4 12.35C14.8 9.7 16.5 8.5 18.1 8.5Z" />
    </svg>
  );
}
